import type { Api } from './api'
import type { TrackedEvent, WidgetSettings } from './types'

/**
 * Emits every interaction twice, on purpose.
 *
 *  - To GA4, so chatbot activity sits alongside the rest of the site's analytics.
 *  - To this app's own event store, which powers the admin dashboard.
 *
 * The redundancy is deliberate: GA4 breaks silently — a container swapped, consent mode
 * misconfigured, an ad blocker — and when someone asks "which prompts do people use most",
 * the answer should not depend on that having been set up correctly.
 *
 * Server events are queued and flushed in batches, with a final `sendBeacon` on unload so
 * the last click of a visit isn't the one that gets lost.
 */
export class Analytics {
  private queue: TrackedEvent[] = []
  private timer: number | null = null

  constructor(
    private readonly api: Api,
    private readonly settings: WidgetSettings,
  ) {
    const flush = (): void => this.flushBeacon()

    // pagehide is the reliable one; visibilitychange covers mobile tab switching, where
    // pagehide may never fire before the process is frozen.
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flush()
      }
    })
  }

  track(event: TrackedEvent): void {
    this.toGa4(event)

    this.queue.push({ ...event, occurred_at: new Date().toISOString() })

    // Batch, but never sit on an event for long — a visitor may leave at any moment.
    this.timer ??= window.setTimeout(() => this.flush(), 2_000)

    if (this.queue.length >= 20) {
      this.flush()
    }
  }

  private flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }

    if (this.queue.length === 0 || !this.api.hasSession()) {
      return
    }

    const batch = this.queue.splice(0, this.queue.length)

    void this.api.sendEvents(batch)
  }

  private flushBeacon(): void {
    if (this.queue.length === 0) {
      return
    }

    const batch = this.queue.splice(0, this.queue.length)

    this.api.beaconEvents(batch)
  }

  /**
   * Pushes to the GTM dataLayer when one exists, and falls back to a direct gtag call.
   * Doing both would double-count, so dataLayer wins where a container is present.
   */
  private toGa4(event: TrackedEvent): void {
    if (!this.settings.ga4_enabled) {
      return
    }

    const params: Record<string, unknown> = {
      chatbot_event: event.type,
      chatbot_tracking_code: event.tracking_code ?? null,
      chatbot_node: event.node_key ?? null,
      chatbot_label: event.label ?? null,
      chatbot_path: event.path ?? null,
    }

    const w = window as typeof window & {
      dataLayer?: unknown[]
      gtag?: (...args: unknown[]) => void
    }

    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event: `chatbot_${event.type}`, ...params })

      return
    }

    w.gtag?.('event', `chatbot_${event.type}`, params)
  }
}
